import { Dialog as DialogPrimitive } from "@base-ui/react/dialog";
import type { VariantProps } from "class-variance-authority";
import * as React from "react";

import { cn } from "../../utils/cn";

import { textVariants } from "./text";

function Dialog({ ...props }: DialogPrimitive.Root.Props): React.ReactElement {
    return <DialogPrimitive.Root data-slot="dialog" {...props} />;
}

function DialogTrigger({
    ...props
}: DialogPrimitive.Trigger.Props): React.ReactElement {
    return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />;
}

function DialogPortal({
    ...props
}: DialogPrimitive.Portal.Props): React.ReactElement {
    return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />;
}

function DialogClose({
    ...props
}: DialogPrimitive.Close.Props): React.ReactElement {
    return <DialogPrimitive.Close data-slot="dialog-close" {...props} />;
}

/**
 * DialogCloseButton — top-right X affordance used in dialog headers. Wraps
 * DialogPrimitive.Close so it inherits the dialog's close semantics (no need
 * for a manual onClick if the dialog is controlled via `onOpenChange`). Accepts
 * an explicit `onClick` for sites that need to fire side effects on cancel.
 */
function DialogCloseButton({
    className,
    onClick,
    disabled,
    ...props
}: Omit<DialogPrimitive.Close.Props, "render"> & {
    disabled?: boolean;
}): React.ReactElement {
    return (
        <DialogPrimitive.Close
            data-slot="dialog-close-button"
            aria-label="Close"
            onClick={onClick}
            disabled={disabled}
            className={cn(
                "absolute top-3.5 right-3.5 p-1 text-foreground-imprint hover:text-foreground-base disabled:opacity-50 disabled:cursor-not-allowed",
                className
            )}
            {...props}
        >
            <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                aria-hidden="true"
            >
                <path d="M18 6L6 18M6 6l12 12" />
            </svg>
        </DialogPrimitive.Close>
    );
}

function DialogOverlay({
    className,
    ...props
}: DialogPrimitive.Backdrop.Props): React.ReactElement {
    return (
        <DialogPrimitive.Backdrop
            data-slot="dialog-overlay"
            className={cn(
                "fixed inset-0 z-40 bg-surface-dialog-base supports-backdrop-filter:backdrop-blur-sm",
                "data-open:animate-in data-open:fade-in-0",
                "data-closed:animate-out data-closed:fade-out-0",
                className
            )}
            {...props}
        />
    );
}

type DialogStatus = "idle" | "pending" | "success" | "error";

interface DialogContentProps extends DialogPrimitive.Popup.Props {
    /** When provided, replaces the default top accent border with an
     *  animated DialogLightBar reflecting transaction state. Omit to keep
     *  the static brand border unchanged (legacy behavior). */
    status?: DialogStatus;
}

function DialogContent({
    className,
    children,
    status,
    ...props
}: DialogContentProps): React.ReactElement {
    return (
        <DialogPortal>
            <DialogOverlay />
            <DialogPrimitive.Popup
                data-slot="dialog-content"
                data-status={status}
                className={cn(
                    "fixed top-1/2 left-1/2 z-50 grid w-full max-w-sm -translate-x-1/2 -translate-y-1/2 gap-4 bg-surface-overlay font-sans text-foreground-base duration-100 outline-none",
                    status === undefined && "border-t-2 border-accent",
                    "data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95",
                    "data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95",
                    className
                )}
                {...props}
            >
                {children}
                {status !== undefined && <DialogLightBar status={status} />}
            </DialogPrimitive.Popup>
        </DialogPortal>
    );
}

/* ── DialogLightBar ─────────────────────────────────────────────
 * Animated 2px bar pinned to the top of the dialog popup. Mirrors
 * ButtonLightBar but with always-visible idle accent (no hover state)
 * and 2px height to match the dialog's existing trim aesthetic. */

const DIALOG_LIGHTBAR_KEYFRAMES = `
@keyframes dialog-lightbar-pending {
    0%   { transform: scaleX(0); animation-timing-function: cubic-bezier(0.0, 0.0, 0.2, 1); }
    20%  { transform: scaleX(1); animation-timing-function: cubic-bezier(0.8, 0.0, 1.0, 1); }
    100% { transform: scaleX(0); }
}
@keyframes dialog-lightbar-success {
    0%   { transform: scaleX(0); }
    100% { transform: scaleX(1); }
}
@keyframes dialog-lightbar-error-burst {
    0%    { opacity: 0; }
    12.5% { opacity: 1; }
    25%   { opacity: 0; }
    37.5% { opacity: 1; }
    50%   { opacity: 0; }
    62.5% { opacity: 1; }
    75%   { opacity: 0; }
    100%  { opacity: 1; }
}
@keyframes dialog-lightbar-error-pulse {
    0%, 40% { opacity: 1; }
    70%     { opacity: 0.1; }
    100%    { opacity: 1; }
}
`;

function DialogLightBar({
    status = "idle"
}: {
    status?: DialogStatus;
}): React.ReactElement {
    return (
        <>
            <style
                dangerouslySetInnerHTML={{ __html: DIALOG_LIGHTBAR_KEYFRAMES }}
            />

            {status === "idle" && (
                <span className="pointer-events-none absolute top-0 left-0 h-[2px] w-full bg-accent" />
            )}

            {status === "pending" && (
                <>
                    <span
                        className="pointer-events-none absolute top-0 left-0 h-[2px] w-1/2 origin-right"
                        style={{
                            backgroundColor: "rgb(var(--color-warning-base))",
                            animation:
                                "dialog-lightbar-pending 2.5s linear infinite"
                        }}
                    />
                    <span
                        className="pointer-events-none absolute top-0 right-0 h-[2px] w-1/2 origin-left"
                        style={{
                            backgroundColor: "rgb(var(--color-warning-base))",
                            animation:
                                "dialog-lightbar-pending 2.5s linear infinite"
                        }}
                    />
                </>
            )}

            {status === "success" && (
                <>
                    <span
                        className="pointer-events-none absolute top-0 left-0 h-[2px] w-1/2 origin-right"
                        style={{
                            backgroundColor: "rgb(var(--color-success-base))",
                            animation:
                                "dialog-lightbar-success 400ms ease-out forwards"
                        }}
                    />
                    <span
                        className="pointer-events-none absolute top-0 right-0 h-[2px] w-1/2 origin-left"
                        style={{
                            backgroundColor: "rgb(var(--color-success-base))",
                            animation:
                                "dialog-lightbar-success 400ms ease-out forwards"
                        }}
                    />
                </>
            )}

            {status === "error" && (
                <span
                    className="pointer-events-none absolute top-0 left-0 h-[2px] w-full"
                    style={{
                        backgroundColor: "rgb(var(--color-error-base))",
                        animation:
                            "dialog-lightbar-error-burst 600ms ease-out forwards, dialog-lightbar-error-pulse 2.5s ease-in-out 600ms infinite"
                    }}
                />
            )}
        </>
    );
}

function DialogHeader({
    className,
    ...props
}: React.HTMLAttributes<HTMLDivElement>): React.ReactElement {
    return (
        <div
            data-slot="dialog-header"
            className={cn(
                "flex flex-col gap-1.5 px-4 pt-4 text-left",
                className
            )}
            {...props}
        />
    );
}

function DialogFooter({
    className,
    ...props
}: React.HTMLAttributes<HTMLDivElement>): React.ReactElement {
    return (
        <div
            data-slot="dialog-footer"
            className={cn(
                "flex flex-col-reverse gap-2 px-4 pb-4 sm:flex-row sm:justify-end",
                className
            )}
            {...props}
        />
    );
}

function DialogTitle({
    className,
    variant = "h2",
    ...props
}: DialogPrimitive.Title.Props & {
    /** Typography variant for the title. Defaults to `h2` to match
     *  the legacy modal scale; pass `h1` for an editorial/destructive
     *  modal where the title should carry more weight. */
    variant?: VariantProps<typeof textVariants>["variant"];
}): React.ReactElement {
    return (
        <DialogPrimitive.Title
            data-slot="dialog-title"
            className={cn(
                textVariants({ variant }),
                "text-foreground-base",
                className
            )}
            {...props}
        />
    );
}

function DialogDescription({
    className,
    ...props
}: DialogPrimitive.Description.Props): React.ReactElement {
    return (
        <DialogPrimitive.Description
            data-slot="dialog-description"
            className={cn(
                textVariants({ variant: "body" }),
                "text-foreground-neutral",
                className
            )}
            {...props}
        />
    );
}

export {
    Dialog,
    DialogTrigger,
    DialogPortal,
    DialogClose,
    DialogCloseButton,
    DialogOverlay,
    DialogContent,
    DialogHeader,
    DialogFooter,
    DialogTitle,
    DialogDescription,
    DialogLightBar
};
export type { DialogStatus, DialogContentProps };
